import {apolloClient, gql} from '../graphql';
import {normalizeGraphQLObject} from '../graphql/helpers';
import {formatIsoDate, parseIsoDate} from 'shared/src/datetime';
import type {ID, Transaction, TransactionFilters} from 'shared/src/models';

export const getTransaction = async (id: ID): Promise<Transaction> => {
	const {
		data: {transaction},
		error,
	} = await apolloClient.query({
		query: gql(/* GraphQL */ `
			query GetTransaction($id: ID!) {
				transaction(id: $id) {
					id
					date
					amount
					memo
					payee
					tags {
						id
					}
					projects {
						id
					}
					category {
						id
					}
					sale {
						id
					}
					paymentAccount {
						id
					}
				}
			}
		`),
		variables: {
			id,
		},
		fetchPolicy: 'no-cache',
	});

	if (error) {
		throw error;
	}

	return {
		id: transaction.id,
		memo: transaction.memo,
		payee: transaction.payee ?? null,
		amount: transaction.amount,
		date: parseIsoDate(transaction.date),
		paymentAccount: transaction.paymentAccount.id,
		category: transaction.category.id,
		tags: transaction.tags.map((tag: {id: string}) => tag.id),
		projects: transaction.projects.map((project: {id: string}) => project.id),
		sale: transaction.sale?.id ?? null,
	};
};

export const getTransactions = async (filters: TransactionFilters): Promise<Transaction[]> => {
	const {
		data: {transactions},
		error,
	} = await apolloClient.query({
		query: gql(/* GraphQL */ `
			query GetTransactions(
				$startDate: String
				$endDate: String
				$categoryIds: [ID!]
				$tagIds: [ID!]
				$excludedTagIds: [ID!]
				$projectIds: [ID!]
			) {
				transactions(
					startDate: $startDate
					endDate: $endDate
					categoryIds: $categoryIds
					tagIds: $tagIds
					excludedTagIds: $excludedTagIds
					projectIds: $projectIds
				) {
					id
					date
					amount
					memo
					payee
					tags {
						id
					}
					projects {
						id
					}
					category {
						id
					}
					sale {
						id
					}
					paymentAccount {
						id
					}
				}
			}
		`),
		variables: {
			startDate: filters.startDate ? formatIsoDate(filters.startDate) : undefined,
			endDate: filters.endDate ? formatIsoDate(filters.endDate) : undefined,
			categoryIds: filters.categoryIds,
			tagIds: filters.tagIds,
			excludedTagIds: filters.excludedTagIds,
			projectIds: filters.projectIds,
		},
		fetchPolicy: 'no-cache',
	});

	if (error) {
		throw error;
	}

	return transactions.map((transaction) => ({
		...normalizeGraphQLObject(transaction),
		date: parseIsoDate(transaction.date),
		paymentAccount: transaction.paymentAccount.id,
		category: transaction.category.id,
		tags: transaction.tags.map((tag) => tag.id),
		projects: transaction.projects.map((project) => project.id),
		sale: transaction.sale?.id ?? null,
	}));
};

export const addProjectsToTransactions = async (
	transactionIds: string[],
	projectIds: string[],
): Promise<void> => {
	await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation AddProjectToTransactions($transactionIds: [ID!]!, $projectIds: [ID!]!) {
				addTransactionsToProjects(transactionIds: $transactionIds, projectIds: $projectIds)
			}
		`),
		variables: {transactionIds, projectIds},
	});
};

export const removeProjectsFromTransactions = async (
	transactionIds: string[],
	projectIds: string[],
): Promise<void> => {
	await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation RemoveProjectFromTransactions($transactionIds: [ID!]!, $projectIds: [ID!]!) {
				removeTransactionsFromProjects(transactionIds: $transactionIds, projectIds: $projectIds)
			}
		`),
		variables: {transactionIds, projectIds},
	});
};

export const addTagsToTransactions = async (
	transactionIds: string[],
	tagIds: string[],
): Promise<void> => {
	await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation AddTagToTransactions($transactionIds: [ID!]!, $tagIds: [ID!]!) {
				tagTransactions(transactionIds: $transactionIds, tagIds: $tagIds)
			}
		`),
		variables: {transactionIds, tagIds},
	});
};

export const removeTagsFromTransactions = async (
	transactionIds: string[],
	tagIds: string[],
): Promise<void> => {
	await apolloClient.mutate({
		mutation: gql(/* GraphQL */ `
			mutation RemoveTagFromTransactions($transactionIds: [ID!]!, $tagIds: [ID!]!) {
				untagTransactions(transactionIds: $transactionIds, tagIds: $tagIds)
			}
		`),
		variables: {transactionIds, tagIds},
	});
};
